import * as React from "react";

type SortOrder =  "DESC" | "ASC"

interface GenTableDisplayProps<Item> {
  sort_by?: string;
  sort_order?: SortOrder;
  setListSort: (value: string, direction: SortOrder) => void;
  resetListPages: () => void;
  readListPage: () => void;
  selected: Array<Item>;
  setListSelected: (selected: Array<Item>) => void;
  listData?: Array<Item>;
  columns: Array<{
    calculated_class_name?: (item: Item) => string;
    calculated_value: (item: Item) => React.ReactNode;
    center_column_header?: boolean;
    column_width?: "small" | "medium" | "large" | "extra-large";
    key?: string;
    sort_column: string;
    title: string;
  }>;
}

export default function GenTableDisplay<Item extends {id: number}>(
  {
    sort_by,
    sort_order,
    setListSort,
    resetListPages,
    readListPage,
    selected,
    setListSelected,
    columns,
    listData,
  }: GenTableDisplayProps<Item>,
) {
  const updateSort = (newSortValue: string) => {
    if (!newSortValue) { return null; }

    const newSortDirection: SortOrder = sort_by === newSortValue && sort_order === "ASC" ? "DESC" : "ASC";

    setListSort(newSortValue, newSortDirection);
    resetListPages();
    readListPage();
  };

  const onItemClick = (item: Item) => {
    const isSelected = selected.some((sItem) => sItem.id === item.id);
    const newSelectedItems = isSelected ? selected.filter((sItem) => sItem.id !== item.id) : selected.concat([item]);
    setListSelected(newSelectedItems);
  };

  const tableHeaderCells = columns.map((column) => {
    let orderClass;
    let className = "";
    if (column.center_column_header) {
      className = `${className} edit-documents-access-table-centered-header`;
    }

    if (column.sort_column) {
      className = `${className} sort_cell`;
      if (column.sort_column !== sort_by) {
        orderClass = "fa fa-sort";
      } else if (sort_order === "ASC") {
        orderClass = "fa fa-sort-up";
      } else {
        orderClass = "fa fa-sort-down";
      }
    }

    if (column.column_width) {
      className = `${className} ${column.column_width}-column-size`;
    }

    return (
      <td key={column.key || column.title} onClick={() => {updateSort(column.sort_column);}} className={className}>
        {column.title}
        <span>  <i className={orderClass} /></span>
      </td>
    );
  });

  const tableHeader = (
    <tr key='table-header'>
      <td key="select-box" className="select-box" />
      {tableHeaderCells}
    </tr>
  );

  const itemRows = (listData || []).map((item) => {
    const cells = columns.map((column) => {
      const className = (column.calculated_class_name) ? column.calculated_class_name(item) : "";
      return <td key={column.key || column.title} className={className}>{column.calculated_value(item)}</td>;
    });

    const isSelected = selected.some((sItem) => sItem.id === item.id);

    return (
      <tr key={item.id} className={isSelected ? "bg-sky-100" : ""}>
        <td key="select-box" className="select-box">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => {onItemClick(item);}}
          />
        </td>
        {cells}
      </tr>
    );
  });

  return (
    <div className="gen-table">
      <table>
        <thead>
          {tableHeader}
        </thead>
        <tbody>
          {itemRows}
        </tbody>
      </table>
    </div>
  );
}
